// Opzioni grafiche della vetrina personalizzata (negozio personale Pro).
// Gli id di palette, layout e voci di menu devono restare allineati con
// backend/shops/catalog.py, che li valida al salvataggio.
//
// I colori sono applicati come variabili CSS (canali RGB) sul contenitore della
// vetrina e letti dalle classi Tailwind `shop-*` (vedi tailwind.config.js).
// Senza variabili impostate le classi ricadono sui colori Rarifex standard.

export const SHOP_PALETTES = [
  { id: 'rarifex', label: 'Rarifex', primary: '#008080', primaryDark: '#006666', accent: '#DAA520', bg: '#F8FAFC' },
  { id: 'terracotta', label: 'Terracotta', primary: '#B5502F', primaryDark: '#8E3C22', accent: '#D9A441', bg: '#FBF6F1' },
  { id: 'oliva', label: 'Oliva', primary: '#5B6B2F', primaryDark: '#46531F', accent: '#C08A3E', bg: '#F7F6EF' },
  { id: 'notte', label: 'Blu notte', primary: '#1E3A5F', primaryDark: '#142945', accent: '#C9A227', bg: '#F5F7FA' },
  { id: 'salvia', label: 'Salvia', primary: '#4F7A68', primaryDark: '#3B5D4F', accent: '#C47F5A', bg: '#F4F7F4' },
  { id: 'rosa_antico', label: 'Rosa antico', primary: '#A1555F', primaryDark: '#7F3F48', accent: '#C9A06B', bg: '#FBF5F4' },
  { id: 'lavanda', label: 'Lavanda', primary: '#6B5B95', primaryDark: '#524475', accent: '#D4A373', bg: '#F7F5FA' },
  { id: 'sabbia', label: 'Sabbia', primary: '#8A6A45', primaryDark: '#6B5134', accent: '#3F6E73', bg: '#FAF7F2' },
];

export const SHOP_LAYOUTS = [
  { id: 'classico', label: 'Classico', description: 'Copertina a tutto schermo e titoli con grazie: il negozio di tradizione' },
  { id: 'galleria', label: 'Galleria', description: 'Foto grandi e collage in apertura: per chi punta sulle immagini' },
  { id: 'minimale', label: 'Minimale', description: 'Niente copertina, tanto spazio bianco, maiuscoletto spaziato' },
  { id: 'editoriale', label: 'Editoriale', description: 'Come una rivista: corsivi, impaginazione asimmetrica, didascalie' },
  { id: 'contemporaneo', label: 'Contemporaneo', description: 'Maiuscole grandi, blocchi di colore e bordi netti' },
  { id: 'boutique', label: 'Boutique', description: 'Elegante e centrato: caratteri sottili, linee dorate, atmosfera di lusso' },
  { id: 'catalogo', label: 'Catalogo', description: 'Prodotti subito in primo piano: per chi ha molte creazioni' },
  { id: 'naturale', label: 'Naturale', description: 'Forme morbide e arrotondate, toni caldi e accoglienti' },
];

// Caratteri tipografici dei modelli, caricati solo quando servono
const LAYOUT_FONTS = {
  classico: 'Playfair+Display:wght@400;600;700',
  editoriale: 'Fraunces:ital,opsz,wght@0,9..144,300..700;1,9..144,300..700',
  contemporaneo: 'Space+Grotesk:wght@400;500;700',
  boutique: 'Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,400',
  naturale: 'Nunito:wght@400;600;700;800',
};

export const ensureShopFont = (layout) => {
  const family = LAYOUT_FONTS[layout];
  if (!family || typeof document === 'undefined') return;
  const id = `shop-font-${layout}`;
  if (document.getElementById(id)) return;
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${family}&display=swap`;
  document.head.appendChild(link);
};

// Voci di menu disponibili. `section` è l'id della sezione nella pagina vetrina;
// "su_misura" non è una sezione ma apre la richiesta su misura diretta.
export const SHOP_MENU_ITEMS = {
  laboratorio: { label: 'La bottega', section: 'intro', hint: 'Galleria, descrizione e storia' },
  prodotti: { label: 'Prodotti e Creazioni', section: 'prodotti', required: true },
  collezioni: { label: 'Collezioni', section: 'collezioni', hint: 'Raccolte tematiche delle tue creazioni: le prepari nella scheda «Collezioni». Compare solo se ne hai almeno una' },
  su_misura: { label: 'Su misura', section: null, hint: 'Accetti lavori su commissione? Se la disattivi spariscono i pulsanti e non ricevi richieste su misura dirette' },
  portfolio: { label: 'Portfolio', section: 'portfolio' },
  social: { label: 'Seguimi', section: 'social' },
  recensioni: { label: 'Recensioni', section: 'recensioni', required: true },
};

export const DEFAULT_MENU = Object.keys(SHOP_MENU_ITEMS).map((key) => ({ key, enabled: true }));
export const DEFAULT_BRANDING = { palette: 'rarifex', layout: 'classico', logo_url: null };
export const MAX_FEATURED = 8;
export const MAX_COLLECTIONS = 12;
export const MAX_COLLECTION_PRODUCTS = 60;

const hexToChannels = (hex) => {
  const h = hex.replace('#', '');
  const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16);
  return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
};

export const getPalette = (id) => SHOP_PALETTES.find((p) => p.id === id) || SHOP_PALETTES[0];

// Stile da applicare al contenitore della vetrina. Null = colori standard.
export const shopThemeStyle = (branding) => {
  if (!branding) return undefined;
  const p = getPalette(branding.palette);
  return {
    '--shop-primary': hexToChannels(p.primary),
    '--shop-primary-dark': hexToChannels(p.primaryDark),
    '--shop-accent': hexToChannels(p.accent),
    '--shop-bg': hexToChannels(p.bg),
  };
};

// Normalizza un menu salvato: voci sconosciute scartate, mancanti aggiunte in coda,
// obbligatorie sempre attive (stesse regole del backend).
export const normalizeMenu = (menu) => {
  const seen = new Set();
  const out = [];
  (Array.isArray(menu) ? menu : []).forEach((m) => {
    if (!m || !SHOP_MENU_ITEMS[m.key] || seen.has(m.key)) return;
    seen.add(m.key);
    out.push({ key: m.key, enabled: SHOP_MENU_ITEMS[m.key].required ? true : m.enabled !== false });
  });
  Object.keys(SHOP_MENU_ITEMS).forEach((key) => {
    if (!seen.has(key)) out.push({ key, enabled: true });
  });
  return out;
};

// Classi dei modelli grafici nella vetrina del marketplace e nell'anteprima dell'editor.
export const layoutClasses = (layout) => {
  switch (layout) {
    case 'galleria':
      return {
        hero: 'relative h-72 md:h-[30rem]',
        headerCard: 'shadow-xl',
        productGrid: 'grid grid-cols-1 sm:grid-cols-2 gap-6',
        productImage: 'aspect-[4/5]',
        productCard: 'overflow-hidden hover:shadow-xl transition-all group cursor-pointer border-0 shadow-sm h-full flex flex-col',
        imageHover: 'group-hover:scale-105',
        heading: 'text-2xl md:text-3xl font-bold text-slate-800',
        hideHeroImage: false,
      };
    case 'minimale':
      return {
        hero: 'relative h-28 md:h-32',
        headerCard: 'shadow-none border border-slate-200',
        productGrid: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10',
        productImage: 'aspect-square',
        productCard: 'overflow-hidden transition-all group cursor-pointer border-0 shadow-none bg-transparent h-full flex flex-col',
        imageHover: 'group-hover:opacity-90',
        heading: 'text-xl md:text-2xl font-light tracking-wide uppercase text-slate-800',
        hideHeroImage: true,
      };
    case 'catalogo':
      return {
        hero: 'relative h-40 md:h-48',
        headerCard: 'shadow-md',
        productGrid: 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3',
        productImage: 'aspect-square',
        productCard: 'overflow-hidden hover:shadow-lg transition-all group cursor-pointer border border-slate-200 shadow-none h-full flex flex-col',
        imageHover: 'group-hover:scale-105',
        heading: 'text-xl font-bold text-slate-800',
        hideHeroImage: false,
      };
    case 'contemporaneo':
      return {
        hero: 'relative h-64 md:h-80',
        headerCard: 'shadow-none border-2 border-slate-900 !rounded-none',
        productGrid: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5',
        productImage: 'aspect-square',
        productCard: 'overflow-hidden transition-all group cursor-pointer border-2 border-slate-900 !rounded-none shadow-none h-full flex flex-col hover:-translate-y-1',
        imageHover: 'group-hover:scale-105',
        heading: 'text-2xl md:text-3xl font-bold uppercase tracking-tight text-slate-900',
        hideHeroImage: false,
      };
    case 'boutique':
    case 'editoriale':
      return {
        hero: 'relative h-72 md:h-[26rem]',
        headerCard: 'shadow-none border border-slate-200',
        productGrid: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10',
        productImage: 'aspect-[4/5]',
        productCard: 'overflow-hidden transition-all group cursor-pointer border-0 shadow-none bg-transparent h-full flex flex-col',
        imageHover: 'group-hover:scale-105',
        heading: 'text-2xl md:text-3xl font-normal italic text-slate-800',
        hideHeroImage: false,
      };
    case 'naturale':
      return {
        hero: 'relative h-64 md:h-80',
        headerCard: 'shadow-lg !rounded-[2rem]',
        productGrid: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5',
        productImage: 'aspect-square',
        productCard: 'overflow-hidden hover:shadow-xl transition-all group cursor-pointer border-0 shadow-sm !rounded-[1.75rem] h-full flex flex-col',
        imageHover: 'group-hover:scale-105',
        heading: 'text-2xl font-extrabold text-slate-800',
        hideHeroImage: false,
      };
    case 'classico':
    default:
      return {
        hero: 'relative h-64 md:h-80',
        headerCard: 'shadow-xl',
        productGrid: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4',
        productImage: 'aspect-square',
        productCard: 'overflow-hidden hover:shadow-xl transition-all group cursor-pointer border-0 shadow-sm h-full flex flex-col',
        imageHover: 'group-hover:scale-110',
        heading: 'text-2xl font-bold text-slate-800',
        hideHeroImage: false,
      };
  }
};

// Tipografia e struttura della MODALITÀ NEGOZIO per ciascun modello grafico.
// Oltre alle classi, ogni modello dichiara alcune scelte di impaginazione (hero,
// allineamento, forme) lette dalle pagine del negozio: così i modelli differiscono
// davvero nella struttura e non solo nei colori.
const BASE_STYLE = {
  centered: false,          // testi e intestazioni di sezione centrati
  centeredHeader: false,    // logo al centro e menu sotto
  transparentHeader: false, // intestazione trasparente sopra la copertina in home
  hero: 'cover',            // variante di apertura della home
  radius: 'rounded-2xl',    // forma delle foto nelle schede prodotto
  media: 'rounded-2xl',     // forma delle immagini grandi (storia, categorie, laboratorio)
  btn: 'rounded-full',      // forma dei pulsanti
  panel: 'bg-white shadow-sm rounded-2xl', // riquadri (recensioni, passi, contatti)
  band: 'bg-white',         // fascia alternata delle sezioni
  font: '',                 // carattere del testo (vuoto = Inter del sito)
  cardMeta: 'mt-1',
  price: 'font-semibold text-shop-primary',
  productsFirst: false,     // in home le creazioni vengono prima di tutto
  showcase: 8,
};

const STYLES = {
  classico: {
    heading: "font-['Playfair_Display',Georgia,serif] font-semibold",
    display: "font-['Playfair_Display',Georgia,serif] text-4xl md:text-6xl font-semibold",
    header: 'bg-white text-slate-900 border-b border-slate-200',
    headerLink: 'text-slate-600 hover:text-shop-primary',
    headerLinkActive: 'text-shop-primary border-b-2 border-shop-primary',
    grid: 'grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6',
    image: 'aspect-square',
    card: 'group block bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow',
    cardTitle: "px-4 pt-3 font-['Playfair_Display',Georgia,serif] text-lg text-slate-900",
    cardMeta: 'px-4 pb-4 mt-1',
    radius: 'rounded-none',
    transparentHeader: true,
    hero: 'cover',
  },
  galleria: {
    heading: 'font-bold tracking-tight',
    display: 'text-4xl md:text-6xl font-bold tracking-tight',
    header: 'bg-shop-primary text-white',
    headerLink: 'text-white/80 hover:text-white',
    headerLinkActive: 'text-white border-b-2 border-white',
    grid: 'grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-8',
    image: 'aspect-[4/5]',
    card: 'group block',
    cardTitle: 'mt-3 text-base font-semibold text-slate-900',
    radius: 'rounded-lg',
    hero: 'collage',
    showcase: 4,
  },
  minimale: {
    media: 'rounded-none',
    heading: 'font-light uppercase tracking-[0.18em]',
    display: 'text-3xl md:text-5xl font-light uppercase tracking-[0.2em]',
    header: 'bg-shop-bg text-slate-900 border-b border-slate-200',
    headerLink: 'text-slate-500 hover:text-slate-900 uppercase tracking-[0.15em] text-xs',
    headerLinkActive: 'text-slate-900 uppercase tracking-[0.15em] text-xs',
    grid: 'grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12',
    image: 'aspect-square',
    card: 'group block',
    cardTitle: 'mt-4 text-sm uppercase tracking-[0.12em] text-slate-800 text-center',
    cardMeta: 'justify-center mt-1',
    price: 'text-sm text-slate-600',
    centered: true,
    centeredHeader: true,
    radius: 'rounded-lg',
    panel: 'border border-slate-200 rounded-2xl',
    band: '',
    hero: 'centered',
  },
  editoriale: {
    media: 'rounded-none',
    heading: "font-['Fraunces',Georgia,serif] font-normal italic",
    display: "font-['Fraunces',Georgia,serif] text-5xl md:text-7xl font-light italic leading-[0.95]",
    header: 'bg-shop-bg text-slate-900 border-b border-slate-900',
    headerLink: "text-slate-600 hover:text-slate-900 font-['Fraunces',Georgia,serif] italic text-base",
    headerLinkActive: "text-slate-900 font-['Fraunces',Georgia,serif] italic text-base underline underline-offset-8 decoration-1",
    grid: 'grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-14',
    image: 'aspect-[3/4]',
    card: 'group block',
    cardTitle: "mt-4 font-['Fraunces',Georgia,serif] text-xl italic text-slate-900",
    price: 'text-sm text-slate-500 tracking-wide',
    radius: 'rounded-none',
    btn: 'rounded-none',
    panel: 'border-t border-slate-900 pt-6',
    band: '',
    hero: 'editorial',
  },
  contemporaneo: {
    media: 'rounded-none',
    heading: "font-['Space_Grotesk',Inter,sans-serif] font-bold uppercase tracking-tight",
    display: "font-['Space_Grotesk',Inter,sans-serif] text-5xl md:text-8xl font-bold uppercase tracking-tighter leading-[0.9]",
    header: 'bg-white text-slate-900 border-b-2 border-slate-900',
    headerLink: "text-slate-900 hover:bg-shop-primary hover:text-white px-2 font-['Space_Grotesk',Inter,sans-serif] uppercase text-xs font-bold tracking-wider",
    headerLinkActive: "bg-slate-900 text-white px-2 font-['Space_Grotesk',Inter,sans-serif] uppercase text-xs font-bold tracking-wider",
    grid: 'grid grid-cols-2 lg:grid-cols-4 gap-4',
    image: 'aspect-square',
    card: 'group block border-2 border-slate-900 bg-white transition-all hover:-translate-x-1 hover:-translate-y-1 hover:shadow-[6px_6px_0_0_rgb(15,23,42)]',
    cardTitle: "px-3 pt-3 font-['Space_Grotesk',Inter,sans-serif] font-bold uppercase text-sm text-slate-900",
    cardMeta: 'px-3 pb-3 mt-1',
    price: "font-['Space_Grotesk',Inter,sans-serif] font-bold text-slate-900",
    radius: 'rounded-none',
    btn: 'rounded-none',
    panel: 'border-2 border-slate-900 bg-white',
    hero: 'block',
  },
  boutique: {
    media: 'rounded-none',
    heading: "font-['Cormorant_Garamond',Georgia,serif] font-medium tracking-wide",
    display: "font-['Cormorant_Garamond',Georgia,serif] text-5xl md:text-7xl font-light tracking-[0.08em]",
    header: 'bg-white text-slate-900',
    headerLink: "text-slate-500 hover:text-slate-900 font-['Cormorant_Garamond',Georgia,serif] text-base tracking-[0.15em] uppercase",
    headerLinkActive: "text-slate-900 font-['Cormorant_Garamond',Georgia,serif] text-base tracking-[0.15em] uppercase border-b border-shop-accent",
    grid: 'grid grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14',
    image: 'aspect-[4/5]',
    card: 'group block',
    cardTitle: "mt-5 font-['Cormorant_Garamond',Georgia,serif] text-xl text-slate-900 text-center",
    cardMeta: 'justify-center mt-1',
    price: "font-['Cormorant_Garamond',Georgia,serif] text-lg text-shop-accent",
    centered: true,
    centeredHeader: true,
    transparentHeader: true,
    radius: 'rounded-none',
    btn: 'rounded-none',
    panel: 'border border-shop-accent/40 bg-white',
    band: 'bg-white',
    hero: 'boutique',
  },
  catalogo: {
    heading: 'font-bold',
    display: 'text-3xl md:text-4xl font-bold',
    header: 'bg-white text-slate-900 border-b border-slate-200',
    headerLink: 'text-slate-600 hover:text-shop-primary',
    headerLinkActive: 'text-shop-primary font-semibold',
    grid: 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4',
    image: 'aspect-square',
    card: 'group block bg-white rounded-lg overflow-hidden border border-slate-200 hover:border-shop-primary hover:shadow-md transition-all',
    cardTitle: 'px-3 pt-2.5 text-sm font-medium text-slate-900',
    cardMeta: 'px-3 pb-3 mt-0.5',
    radius: 'rounded-none',
    panel: 'bg-white border border-slate-200 rounded-xl',
    hero: 'compact',
    productsFirst: true,
    showcase: 10,
  },
  naturale: {
    media: 'rounded-[2rem]',
    heading: "font-['Nunito',Inter,sans-serif] font-extrabold",
    display: "font-['Nunito',Inter,sans-serif] text-4xl md:text-6xl font-extrabold",
    header: 'bg-shop-bg text-slate-900',
    headerLink: "text-slate-600 hover:text-shop-primary hover:bg-shop-primary/10 px-3 rounded-full font-['Nunito',Inter,sans-serif] font-semibold",
    headerLinkActive: "bg-shop-primary/15 text-shop-primary px-3 rounded-full font-['Nunito',Inter,sans-serif] font-bold",
    grid: 'grid grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6',
    image: 'aspect-square',
    card: 'group block bg-white rounded-[1.75rem] overflow-hidden shadow-sm hover:shadow-xl transition-shadow p-2',
    cardTitle: "px-3 pt-3 font-['Nunito',Inter,sans-serif] font-bold text-slate-900",
    cardMeta: 'px-3 pb-3 mt-0.5',
    radius: 'rounded-[1.5rem]',
    panel: 'bg-white rounded-[2rem] shadow-sm',
    font: "font-['Nunito',Inter,sans-serif]",
    hero: 'organic',
  },
};

export const shopModeStyle = (layout) => {
  const id = STYLES[layout] ? layout : 'classico';
  return { ...BASE_STYLE, ...STYLES[id], id };
};

// Pagine del negozio collegate alle voci di menu
export const SHOP_PAGES = {
  laboratorio: 'laboratorio',
  prodotti: 'prodotti',
  collezioni: 'collezioni',
  su_misura: 'su-misura',
  portfolio: 'portfolio',
  social: 'contatti',
  recensioni: 'recensioni',
};
